import {selectGameType} from '../redux/reducers/sharedStateReducer/selectors';
import {GAME_TYPES} from '../constants/game';
import {FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER} from '../configs/playbackSpeed';
import {BOT_ACTION_INTERVAL_MS} from '../utils/bots/botConfig';
import {getBotActionIntervalMs} from '../utils/bots/botActionPace';
import {getReplayPlaybackSpeedMultiplier} from '../utils/replay/replayPlaybackSpeed';
import {COINS_SPENT_NUMBER} from '../configs/coinsSpentAnimation';

/**
 * Потраченные монеты над ячейкой: всплывающее «−N» с иконкой монет (см.
 * HexGridRenderer.showCoinsSpentNumber). Зовется вместе со звуком монет:
 *  - на приземлении нанятого отряда и на докупке его размера (см.
 *    playUnitRecruitmentEffects);
 *  - на приземлении поставленного строения — и запланированного тоже (см.
 *    playBuildingPlacementEffects).
 * И для своего действия, и для чужого (сеть, бот, запись матча).
 *
 * Числа нет:
 *  - когда тратить было нечего (бесплатный найм, бесплатная постройка);
 *  - в скрытой вкладке — кадры там не идут, и все накопившиеся числа всплыли бы
 *    разом на возврате. Числу, которое всплывает на приземлении жетона, этого
 *    мало: жетон, полетевший в скрытой вкладке, садится уже после возврата —
 *    там сверяется, свежа ли трата (см. createLandingCoinsSpentAnimation);
 *  - на перемотке (см. checkIfCoinsSpentAnimationAllowed);
 *  - когда нет поля или ячейки.
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {Object} params
 * @param {string} params.hexKey — ячейка, где стоит отряд или строение
 * @param {number} params.amount — сколько монет потрачено
 * @param {boolean} [params.isPaced] — действие чужое (бот, соперник по сети,
 *   запись матча): его темп задает не игрок за этим экраном
 * @returns {boolean} показано ли число
 */
export const playCoinsSpentAnimation = (store, hexGridRenderer, {hexKey, amount, isPaced = false, isGain = false} = {}) => {
  if (!hexGridRenderer || typeof hexGridRenderer.showCoinsSpentNumber !== 'function') {
    return false;
  }
  if (typeof document !== 'undefined' && document.hidden) {
    return false;
  }
  if (!checkIfCoinsSpentAnimationAllowed(store.getState(), {isPaced})) {
    return false;
  }

  try {
    return Boolean(isGain
      ? hexGridRenderer.showCoinsSpentNumber(hexKey, amount, {isGain})
      : hexGridRenderer.showCoinsSpentNumber(hexKey, amount));
  } catch (error) {
    // Анимация — украшение: ее поломка не должна съесть остальные эффекты найма
    console.warn('[CoinsSpent] не удалось показать потраченные монеты', error);
    return false;
  }
};

/**
 * Полученные монеты над отрядом: всплывающее «+N» с иконкой монет — то же
 * число, что и у трат, только со знаком плюс. Показывается:
 *  - над отрядом лепреконов, укравшим монеты (сколько украл);
 *  - над гоблином, снявшим свою ловушку (награда).
 * Когда числа нет — те же правила, что у трат (см. playCoinsSpentAnimation).
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {Object} params — как у playCoinsSpentAnimation; amount — сколько получено
 * @returns {boolean} показано ли число
 */
export const playCoinsGainedAnimation = (store, hexGridRenderer, params = {}) => (
  playCoinsSpentAnimation(store, hexGridRenderer, {...params, isGain: true})
);

/**
 * Число, которое всплывает не сразу, а на приземлении жетона (нанятого отряда,
 * поставленного или запланированного строения): трата случилась сейчас, а
 * показать ее надо, когда жетон коснется земли.
 *
 * Полет идет по кадрам, а в скрытой вкладке кадры стоят: жетон замирает в
 * воздухе и садится уже по возвращении — числа всех трат за это время всплыли
 * бы разом, через минуты после самих трат. Поэтому число показывается, только
 * пока трата свежа (COINS_SPENT_NUMBER.maxLandingDelayMs).
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {Object} params — как у playCoinsSpentAnimation
 * @returns {Function} зовется на приземлении; возвращает, показано ли число
 */
export const createLandingCoinsSpentAnimation = (store, hexGridRenderer, params) => {
  const spentAt = Date.now();
  return () => (
    Date.now() - spentAt <= COINS_SPENT_NUMBER.maxLandingDelayMs
      && playCoinsSpentAnimation(store, hexGridRenderer, params)
  );
};

/**
 * Число живет около секунды: на перемотке оно не успевает прочитаться, а числа
 * соседних действий только засыпают поле. Порог тот же, что у остальных
 * анимаций поля (FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER):
 *  - запись матча (страница записи и витрина главного меню) — по ее скорости;
 *  - боты локальной партии (и живая партия ботов на витрине) — по их шагу,
 *    выбранному ползунком скорости. Своего действия это не касается: игрок
 *    действует в собственном темпе.
 * В сетевой партии чужие действия чаще стандартного шага не идут.
 *
 * @param {Object} state
 * @param {Object} [params]
 * @param {boolean} [params.isPaced] — действие чужое
 * @returns {boolean}
 */
export const checkIfCoinsSpentAnimationAllowed = (state, {isPaced = false} = {}) => {
  const gameType = selectGameType(state);
  if (gameType === GAME_TYPES.REPLAY) {
    return getReplayPlaybackSpeedMultiplier() <= FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER;
  }
  if (isPaced && gameType === GAME_TYPES.LOCAL) {
    return BOT_ACTION_INTERVAL_MS / getBotActionIntervalMs() <= FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER;
  }

  return true;
};
